import FocusTrap from 'focus-trap-react';
import React, { FCC, RefObject, useCallback, useRef } from 'react';
import { Popover, PopoverBody } from 'reactstrap';
import { Placement } from 'popper.js';
import { useOutsideEvent } from '../hooks/useOutsideEvent';
import './popper-panel.scss';

const PopperPanel: FCC<{
  placement: Placement;
  targetRef: RefObject<HTMLElement>;
  containerRef: RefObject<HTMLElement>;
  showing: boolean;
  hide: () => void;
  initialFocus?: string | (() => HTMLElement);
}> = ({ placement, targetRef, containerRef, showing, hide, children, initialFocus }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const onOutsideEvent = useCallback(
    (e: Event) => {
      // don't hide it here if clicking on toggle
      if (showing && containerRef.current && !containerRef.current.contains(e.target as HTMLElement)) {
        hide();
      }
    },
    [containerRef, hide, showing]
  );

  useOutsideEvent(contentRef, onOutsideEvent, showing);

  return (
    <Popover
      placement={placement}
      isOpen={showing}
      target={targetRef}
      container={containerRef}
      flip={
        false /* the bio-bubble can change size, so flip can suddenly flip it when e.g. user tries to send meessage*/
      }
    >
      {() =>
        showing && (
          <PopoverBody>
            <FocusTrap
              focusTrapOptions={{
                initialFocus: initialFocus,
                allowOutsideClick: true
              }}
            >
              {/* this is to catch all escapes from inside the popover */
              /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <div
                ref={contentRef}
                role="dialog"
                onKeyUp={(e) => {
                  if (e.key === 'Escape' || e.key === 'Esc') {
                    e.stopPropagation();
                    hide();
                  }
                }}
              >
                {children}
              </div>
            </FocusTrap>
          </PopoverBody>
        )
      }
    </Popover>
  );
};

export default React.memo<typeof PopperPanel>(PopperPanel);
